import { createApp } from "vue";
import { createPinia } from "pinia";
import { createI18n } from "vue-i18n";
import Vue3Toastify, { type ToastContainerOptions } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import "vue3-loading-overlay/dist/vue3-loading-overlay.css";
import "intro.js/minified/introjs.min.css";
import '@bono-packages/dashboard/dist/style.css'
// The widget card and its renderers are styled by the package's own scoped sheet (.bono-widget).
import '@bono-packages/widget/dist/style.css'

import { definePreset } from "@primevue/themes";
import PrimeVue from "primevue/config";
import Aura from '@primevue/themes/aura';
import * as Sentry from "@sentry/vue";

//prime vue stuff
import Button from 'primevue/button';
import Dialog from 'primevue/dialog';
import MultiSelect from 'primevue/multiselect';
import InputText from 'primevue/inputtext';
import InputNumber from 'primevue/inputnumber';
import FloatLabel from "primevue/floatlabel";
import Select from "primevue/select";
import Toast from 'primevue/toast';
import ToastService from 'primevue/toastservice';
import TieredMenu from 'primevue/tieredmenu';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Chip from 'primevue/chip';
import Tag from 'primevue/tag';
import Drawer from 'primevue/drawer';
import Textarea from 'primevue/textarea';
import ToggleButton from 'primevue/togglebutton';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import Checkbox from 'primevue/checkbox';
import ButtonGroup from 'primevue/buttongroup';
import OrderList from 'primevue/orderlist';
import ConfirmationService from 'primevue/confirmationservice';
import ConfirmDialog from 'primevue/confirmdialog';
import Menubar from 'primevue/menubar';
import Skeleton from 'primevue/skeleton';
import Fieldset from 'primevue/fieldset';
import Panel from 'primevue/panel';
import DatePicker from 'primevue/datepicker';
import AutoComplete from 'primevue/autocomplete';
import InputGroup from 'primevue/inputgroup';
import InputGroupAddon from 'primevue/inputgroupaddon';
import Paginator from 'primevue/paginator';
import Menu from 'primevue/menu';
import Popover from 'primevue/popover';
import Divider from 'primevue/divider';
import Listbox from 'primevue/listbox';
import Chart from 'primevue/chart';
import Message from 'primevue/message';
import DialogService from 'primevue/dialogservice';
import DynamicDialog from 'primevue/dynamicdialog';
import Tooltip from 'primevue/tooltip';
import Ripple from 'primevue/ripple';
import ConfirmPopup from 'primevue/confirmpopup';
import Carousel from 'primevue/carousel';
import Card from 'primevue/card';
import VirtualScroller from 'primevue/virtualscroller';
import Password from 'primevue/password';
import ToggleSwitch  from "primevue/toggleswitch";
import Dropdown from "primevue/dropdown";
import InputSwitch from "primevue/inputswitch";

// Permission directive import
import PermissionDirectives from '@/directives/permissions';






// @ts-ignore
//import vue3ToPdf from 'vue3-to-pdf'

import App from "./App.vue";
import router from "./router";

import "./assets/style.css";
import "remixicon/fonts/remixicon.css";
// studio compiles its own Tailwind and scopes every rule under `.bono-studio`, so this sheet is
// inert until a studio screen mounts. It has to be global rather than imported by the host view:
// PocDrawer/PocPopover/PocDialog teleport to <body>, outside any view's subtree.
import "@bono-packages/studio/dist/style.css";
// Same reason for Skills and the chat lane: their drawers and popovers teleport to <body>.
import "@bono-packages/skills/dist/style.css";
import "@bono-packages/chat/dist/style.css";
import { createStudio, createAxiosHttpPort } from "@bono-packages/studio";
import { createSkills } from "@bono-packages/skills";
import { skillsHttp, skillsSse, skillsUpload, skillsApiBase, studioThread } from "@/plugins/skillsPorts";
import { skillsRole } from "@/plugins/skillsRole";
import api from "@/services/api";
import { agentPort } from "@/plugins/agentPort";
// @ts-ignore
import en from "./locales/en.json";
// @ts-ignore
import es from "./locales/es.json";

const availableLocales = import.meta.env.VITE_SUPPORTED_LOCALES.split(",");

const detectBrowserLanguage = () => {
  const language = window.navigator.language;
  const lang = availableLocales.includes(language)
    ? language
    : import.meta.env.VITE_DEFAULT_LOCALE;
  localStorage.setItem("locale", lang);
  return lang;
};

const i18n = createI18n({
  locale: localStorage.getItem("locale")
    ? `${localStorage.getItem("locale")}`
    : detectBrowserLanguage(),
  fallbackLocale: "en",
  legacy: false,
  globalInjection: true,
  messages: { en, es },
});

const app = createApp(App);

Sentry.init({
  app,
  dsn: "https://36cf5a475edd28de273d563c1ec5efe6@o4509355188944896.ingest.us.sentry.io/4509355190124544",
  // Setting this option to true will send default PII data to Sentry.
  // For example, automatic IP address collection on events
  sendDefaultPii: true,
  integrations: [
    Sentry.browserTracingIntegration({ router }),
    Sentry.replayIntegration()
  ],
  // Tracing
  tracesSampleRate: 1.0, // Capture 100% of the transactions
  // Set 'tracePropagationTargets' to control for which URLs distributed tracing should be enabled
  tracePropagationTargets: ["localhost", /^https:\/\/api-test.bono\.eco\//, /^https:\/\/api.bono\.eco\//],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0,// If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.
  environment: import.meta.env.VITE_APP_STAGE, // dev, test, prod
  enabled: ['test', 'prod'].includes(import.meta.env.VITE_APP_STAGE),
});

// main.ts
if (import.meta.env.VITE_APP_STAGE === 'prod') {
  (function(c: any, l: any, a: any, r: any, i: any, t?: any, y?: any) {
    c[a] = c[a] || function() { (c[a].q = c[a].q || []).push(arguments) };
    t = l.createElement(r); t.async = 1; t.src = 'https://www.clarity.ms/tag/' + i;
    y = l.getElementsByTagName(r)[0]; y.parentNode.insertBefore(t, y);
  })(window, document, 'clarity', 'script', 'kructk94h1');
}


app.use(ConfirmationService);
app.use(ToastService);
app.use(DialogService);
app.directive('tooltip', Tooltip);
app.directive('ripple', Ripple);

// Register permission directives
app.use(PermissionDirectives);
app.component('Button', Button);
app.component('ButtonGroup', ButtonGroup)
app.component('Dialog', Dialog);
app.component('MultiSelect', MultiSelect);
app.component('InputText', InputText);
app.component('InputNumber', InputNumber);
app.component('FloatLabel', FloatLabel);
app.component('Select', Select);
app.component('Toast', Toast);
app.component('TieredMenu', TieredMenu);
app.component('DataTable', DataTable);
app.component('Column', Column);
app.component('Chip', Chip);
app.component('Tag', Tag);
app.component('Drawer', Drawer);
app.component('Textarea', Textarea);
app.component('ToggleButton', ToggleButton);
app.component('IconField', IconField);
app.component('InputIcon', InputIcon);
app.component('Checkbox', Checkbox);
app.component('OrderList', OrderList)
app.component("ConfirmDialog", ConfirmDialog);
app.component("Menubar", Menubar);
app.component("Skeleton", Skeleton);
app.component("Fieldset", Fieldset);
app.component("Panel", Panel);
app.component("DatePicker", DatePicker);
app.component("AutoComplete", AutoComplete);
app.component("InputGroup", InputGroup);
app.component("InputGroupAddon", InputGroupAddon);
app.component("Paginator", Paginator);
app.component("Menu", Menu);
app.component("Popover", Popover);
app.component("Divider", Divider);
app.component("Listbox", Listbox);
app.component("Chart", Chart);
app.component("Message", Message);
app.component("DynamicDialog", DynamicDialog);
app.component("ConfirmPopup", ConfirmPopup);
app.component("Carousel", Carousel);
app.component("Card", Card);
app.component("VirtualScroller", VirtualScroller);
app.component("Password", Password);
app.component("ToggleSwitch", ToggleSwitch);
app.component("Dropdown", Dropdown);
app.component("InputSwitch", InputSwitch);


const BonoPreset = definePreset(Aura, {
  semantic: {
    primary: {
      50: "{neutral.50}",
      100: "{neutral.100}",
      200: "{neutral.200}",
      300: "{neutral.300}",
      400: "{neutral.400}",
      500: "{neutral.500}",
      600: "{neutral.600}",
      700: "{neutral.700}",
      800: "{neutral.800}",
      900: "{neutral.900}",
      950: "{neutral.950}",
    }
  },
});

app.use(createPinia());
app.use(router);
app.use(i18n);

/*
 * studio's transport, identity and storage.
 *
 * `api` already owns the base URL, the auth/`X-ID-TOKEN`/`X-COMPANY-ID` headers and the 401 refresh
 * queue, so this is a wrapper and not a second client. `callerApp` is not decoration: studio
 * filters saved reports and stamps rules with it — naming the wrong application here makes every
 * saved report silently invisible. `endpoints.principalValues` is required: this backend serves the
 * path-variable shape (`/principal-values/{companyId}`), not studio's bono-admin default; without
 * the override every principal picker in the access editor is silently empty.
 */
/*
 * Skills for carbon accounting (D7, D197). The browser talks only to saas-api, which relays
 * everything under `/company/{companyId}/skills` and mints the agent's identity itself — the company
 * in the path is the tenant, so nothing here can pick one.
 *
 * `base` is a function because the company switcher can change the tenant without a reload; a base
 * captured once would keep addressing the company the page was opened with. `operations: false` says
 * this is a customer app: no acting-for-another-org switcher, no ops-only verbs.
 */
app.use(createSkills({
  http: skillsHttp,
  sse: skillsSse,
  upload: skillsUpload,
  endpoints: { base: () => skillsApiBase() },
  operations: false,
  role: skillsRole,
  locale: () => localStorage.getItem("locale") ?? "en",
  storage: { prefix: "bono-ca-skills" },
}));

app.use(createStudio({
  http: createAxiosHttpPort(api),
  agent: agentPort,
  // The screens' conversations ride the Skills event stream (D240); without it the explorer and the
  // automations page open with no composer at all and say the app has no assistant.
  thread: studioThread,
  skillsBase: skillsApiBase(),
  callerApp: "CARBON_ACCOUNTING",
  locale: () => localStorage.getItem("locale") ?? "en",
  storage: { prefix: "bono-ca-studio" },
  features: () => ({ localDrafts: false, dryRunWrites: false }),
  endpoints: {
    principalValues: ({ app: forApp, principalType, searchText }) => {
      const companyId = localStorage.getItem("companyId") ?? "";
      const query = new URLSearchParams({ app: forApp, principalType });
      if (searchText) query.set("searchText", searchText);
      return `/access-control/principal-values/${companyId}?${query.toString()}`;
    },
  },
}));
app.use(Vue3Toastify, {
  autoClose: 3000,
  position: "bottom-left",
  dangerouslyHTMLString: true,
} as ToastContainerOptions);
app.use(PrimeVue, {
  theme: {
    preset: BonoPreset,
    options: {
      darkModeSelector: "",
      name: "primevue",
      order: "tailwind-base, primevue, tailwind-utilities",
    },
  },
});

/*
// Global error handler to prevent app crashes
app.config.errorHandler = (error: any, instance: any, info: string) => {
  console.error('Global error handler caught:', error)
  console.error('Component instance:', instance)
  console.error('Error info:', info)
  
  // Don't let the error crash the app or trigger logout
  // Just log it and continue
  return false
}

// Handle unhandled promise rejections
window.addEventListener('unhandledrejection', (event) => {
  console.error('Unhandled promise rejection:', event.reason)
  event.preventDefault() // Prevent the default behavior (logging to console)
})


 */
app.mount("#app");
